import { useState, useEffect, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Moon, Sun } from "lucide-react";
import { navigationItems, NavigationItem  } from "@/types/navigation";
import  Logo  from "@/components/icons/Logo";
import { Button } from "@/components/ui/button";
import { QuoteModal } from "@/components/QuoteModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { fetchData } from "@/lib/supabase-function";
interface NavigationProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}
export function Navigation({ darkMode, toggleDarkMode }: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [item, setItems] = useState<NavigationItem[]>(navigationItems);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);              
    };
    const load = async () => {
          const data = await fetchData(
            "services",
            "title, slug",
            "srno",
            "is_active",
            true,
            false,
            (newData) => {
              setServices(
                newData.map((s) => ({
                  ...s,                  
                }))
              );
            }
          );
      
          setServices(
            data.map((s) => ({
              ...s,              
            }))
          );
          const serviceChildren: NavigationItem[] =
        data?.map((s) => ({
          label: s.title,
          href: `/services/${s.slug}`,
        })) || [];

           setItems((prev) =>
        prev.map((nav) =>
          nav.label === "Services" ? { ...nav, children: serviceChildren } : nav
        )
      );
        };      
        load();  
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActiveRoute = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/98 backdrop-blur-md border-b border-border shadow-soft' 
          : 'bg-background/95 backdrop-blur-md border-b border-border/50'
      }`}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ${
            isScrolled ? 'h-14' : 'h-16'
          }`}>
            {/* Logo */}
            <Link to="/" className="flex items-center px-4 space-x-3">
              <Suspense ><Logo className="w-8 h-8" /></Suspense>
              <span className="font-bold text-xl text-foreground">Core</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {item.map((item) => (
                <div key={item.label} className="relative">
                  {item.children ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className={`flex items-center space-x-1 hover:text-foreground transition-colors ${
                            isActiveRoute(item.href) ? "text-black" : "text-foreground"
                          }`}
                        >
                          <span>{item.label}</span>
                          <ChevronDown className="w-4 h-4 text-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-56 bg-background border border-border shadow-lg">
                        {item.children.map((child) => (
                          <DropdownMenuItem key={child.href} asChild>
                            <Link
                              to={child.href}
                              className={`w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
                                isActiveRoute(child.href) ? "text-black font-medium" : ""
                              }`}
                            >
                              {child.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Link
                      to={item.href}
                      className={`text-sm font-medium hover:text-foreground transition-colors ${
                        isActiveRoute(item.href) ? "text-black dark:text-primary font-medium" : "text-foreground"
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
             <Button
              variant="ghost"
              size="sm"
              onClick={toggleDarkMode}
              className="w-9 h-9 p-0"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <Sun className="w-4 h-4 text-foreground" aria-hidden="true" />
              ) : (
                <Moon className="w-4 h-4 text-foreground" aria-hidden="true" />
              )}
            </Button>


              {/* CTA Button */}
              <Button 
                onClick={() => setIsQuoteModalOpen(true)}
                className="lg:flex btn-hero "
              >
                Get Quote
              </Button>

              {/* Mobile Menu Toggle */}
             
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-background border-t border-border">
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <div key={item.label}>
                  <Link
                    to={item.href}
                    className={`block px-3 py-2 text-sm font-medium rounded-md hover:bg-accent transition-colors ${
                      isActiveRoute(item.href) ? "text-black bg-accent" : "text-foreground"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`block px-3 py-1 text-sm rounded-md hover:bg-accent/50 transition-colors ${
                            isActiveRoute(child.href) ? "text-black" : "text-muted-foreground"
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 mt-4 border-t border-border">
                <Button 
                  onClick={() => {
                    setIsQuoteModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full btn-hero"
                >
                  Get Quote
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </>
  );
}